/**
 * Dorëzimi i një pajisjeje te punonjësi.
 *
 * Gjendja në dorëzim regjistrohet KËTU, jo te kthimi. Pa të, çdo dëmtim i
 * konstatuar në kthim do të ishte fjalë kundër fjale: askush s'e provon dot se
 * pajisja u dha e re apo tashmë e gërvishtur. Prandaj fusha rri e detyrueshme
 * dhe nis nga gjendja aktuale e regjistruar e pajisjes.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  MenuItem, Grid, Alert, Box, Typography, CircularProgress,
} from '@mui/material';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import WorkerPicker from '../common/WorkerPicker';
import { CONDITIONS, assetTitle } from '../../config/assets';
import { activeWorkers } from '../../utils/workerStatus';

/** Data e sotme si "YYYY-MM-DD" — hyn nga jashtë te testet. */
const todayISO = () => new Date().toISOString().slice(0, 10);

export default function AssignAssetDialog({ open, asset, workers = [], onClose, onAssign, today }) {
  const [worker, setWorker]   = useState('');
  const [form, setForm]       = useState({ assignedOn: '', condition: 'good', accessories: '', note: '', documentName: '' });
  const [error, setError]     = useState('');
  const [busy, setBusy]       = useState(false);

  useEffect(() => {
    if (!open) return;
    setWorker('');
    setError('');
    setForm({
      assignedOn: today || todayISO(),
      condition:  asset?.condition || 'good',
      accessories: '', note: '', documentName: '',
    });
  }, [open, asset, today]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Vetëm punëtorët aktivë: një dorëzim te dikush që ka ikur nuk ka kuptim.
  const options = useMemo(
    () => activeWorkers(workers),
    [workers],
  );

  async function submit() {
    if (!worker) { setError('Zgjidh punonjësin të cilit i dorëzohet pajisja.'); return; }
    setBusy(true); setError('');
    try {
      await onAssign({ ...form, workerId: worker });
    } catch (e) {
      setError(e.message || 'Dorëzimi dështoi.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Box sx={{ width: 34, height: 34, borderRadius: 1.5, bgcolor: 'info.soft', color: 'info.onSoft',
                   display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <AssignmentIndIcon sx={{ fontSize: 19 }} />
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: '1rem', lineHeight: 1.2 }}>Dorëzo pajisjen</Typography>
          <Typography variant="caption" color="text.secondary">
            {asset ? `${asset.assetCode} · ${assetTitle(asset)}` : ''}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {!!error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

        <Grid container spacing={1.5}>
          <Grid item xs={12}>
            {/* Kërkim me shkrim: një listë me tërë kompaninë hapet mbi vetë
                dialogun dhe s'ka ku të shkruhet. */}
            <WorkerPicker workers={options} value={worker} onChange={setWorker}
              label="Punonjësi" required />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField type="date" label="Data e dorëzimit" fullWidth size="small"
              InputLabelProps={{ shrink: true }} value={form.assignedOn} onChange={set('assignedOn')} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField select label="Gjendja në dorëzim" fullWidth size="small"
              value={form.condition} onChange={set('condition')}
              helperText="Kjo është pika e krahasimit te kthimi.">
              {CONDITIONS.map((c) => <MenuItem key={c.key} value={c.key}>{c.label}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField label="Pajisjet shtesë" fullWidth size="small"
              value={form.accessories} onChange={set('accessories')}
              placeholder="Karikues, çantë, maus, kabllo HDMI…" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Procesverbal / dokument" fullWidth size="small"
              value={form.documentName} onChange={set('documentName')}
              placeholder="Nr. i procesverbalit të dorëzimit" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField label="Koment" fullWidth size="small" value={form.note} onChange={set('note')} />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5 }}>
        <Button onClick={onClose} disabled={busy} sx={{ textTransform: 'none' }}>Anulo</Button>
        <Button variant="contained" onClick={submit} disabled={busy || !worker}
          startIcon={busy ? <CircularProgress size={15} color="inherit" /> : null}
          sx={{ textTransform: 'none', fontWeight: 700 }}>
          Dorëzo
        </Button>
      </DialogActions>
    </Dialog>
  );
}
